import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { bootOnce } from '../../bootstrap';
import { navigate, requestAppBack } from '../../router/router';
import { IconBack, IconStar } from '../../ui/icons';
import { useI18n } from '../../bootstrap/i18n';
import { getTelegramId } from '../../utils/telegramUser';
import { reportHandledError } from '../../monitoring/uiErrorSink';
import { authedApiJsonOrThrow, authedFetchJson } from '../../utils/api';
import { isNeverShowSet, setNeverShow } from '../quests/QuestSheet';
import { COUNTRIES, countryFlag } from '../../data/countries';

// ============================================================================
// Daily Quest Types
// ============================================================================
type QuestObjective = {
  id: number;
  title_key: string;
  icon: string;
  action_type: string;
  target: number;
  current: number;
  completed: boolean;
};

type DailyQuest = {
  id: number | null;
  template_id?: number;
  date: string;
  title_key?: string;
  description_key?: string;
  reward_credits?: number;
  icon?: string;
  objectives: QuestObjective[];
  all_completed: boolean;
  can_claim: boolean;
  already_claimed: boolean;
  credits_awarded: number;
  quest_unavailable?: boolean;
};

type QuestsData = {
  enabled: boolean;
  date: string;
  quest: DailyQuest | null;
  weekly: {
    days_completed: number;
    required_days: number;
    bonus_credits: number;
    can_claim: boolean;
    already_claimed: boolean;
  } | null;
};

// Quest icon mapping
const QUEST_ICONS: Record<string, string> = {
  chat: '💬',
  eye: '👀',
  video: '🎬',
  star: '⭐',
  fire: '🔥',
  heart: '❤️',
  gift: '🎁',
  trophy: '🏆',
  rocket: '🚀',
  sparkles: '✨',
  crown: '👑',
  gem: '💎',
  lock: '🔓',
  camera: '📸',
  kiss: '💋',
  muscle: '💪',
};

type TaskRow = {
  id: number;
  title: string;
  description: string | null;
  reward_credits: number;
  is_active: boolean;
  type?: string | null;
  url?: string | null;
  outbound_url?: string | null;
  channel_id?: string | null;
  channel_ids?: string[] | null;
};

type CompletedTask = {
  task_id: number;
  credits_awarded: number;
  completed_at: string;
  status?: string;
  instagram_username?: string;
};

type PendingTask = {
  task_id: number;
  status: string;
  instagram_username?: string;
  completed_at: string;
};

type ModalState = {
  show: boolean;
  type: 'success' | 'error';
  message: string;
  reward?: number;
  actions?: Array<{ label: string; onClick: () => void; kind?: 'primary' | 'secondary' }>;
};

function taskTitleKey(taskId: number) {
  return `task.${Number(taskId || 0)}.title`;
}

function taskDescriptionKey(taskId: number) {
  return `task.${Number(taskId || 0)}.description`;
}

function taskFolderChannelCount(task: TaskRow) {
  return Array.isArray(task.channel_ids) ? task.channel_ids.length : null;
}

export function TasksRoute() {
  const services = bootOnce();
  const { locale, t } = useI18n(services.i18n);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [completed, setCompleted] = useState<Set<number>>(new Set());
  const [completedTasks, setCompletedTasks] = useState<CompletedTask[]>([]);
  const [pendingTasks, setPendingTasks] = useState<Map<number, PendingTask>>(new Map());
  const [awardingTaskId, setAwardingTaskId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'available' | 'completed'>('available');
  
  const [modal, setModal] = useState<ModalState>({ show: false, type: 'success', message: '' });

  // ============================================================================
  // Daily Quest State
  // ============================================================================
  const [questsData, setQuestsData] = useState<QuestsData | null>(null);
  const [questLoading, setQuestLoading] = useState(true);
  const [claimingQuest, setClaimingQuest] = useState(false);
  const [claimingWeekly, setClaimingWeekly] = useState(false);
  const [popupDisabled, setPopupDisabled] = useState(() => isNeverShowSet());

  // Load quests
  const loadQuests = useCallback(async () => {
    try {
      setQuestLoading(true);
      const response = await authedFetchJson(services, '/api/quests/today');
      if (response.ok) {
        setQuestsData(response.data as QuestsData);
      }
    } catch (err) {
      console.error('[TasksRoute] Failed to load quests:', err);
    } finally {
      setQuestLoading(false);
    }
  }, [services]);

  // Claim quest reward
  const claimQuestReward = async () => {
    if (claimingQuest || !questsData?.quest?.can_claim) return;
    setClaimingQuest(true);
    const tg = (window as any).Telegram?.WebApp;

    try {
      const response = await authedFetchJson(services, '/api/quests/claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: questsData.quest.date }),
      });

      if (response.ok && (response.data as any)?.ok) {
        await loadQuests();
        window.dispatchEvent(new Event('aicams-credits-refresh'));
        tg?.HapticFeedback?.notificationOccurred?.('success');
        setModal({
          show: true,
          type: 'success',
          message: t('quest.claimed_success', 'Quest reward claimed!'),
          reward: questsData.quest.reward_credits,
        });
      }
    } catch (err) {
      console.error('[TasksRoute] Failed to claim quest:', err);
      setModal({
        show: true,
        type: 'error',
        message: t('quest.claim_error', 'Failed to claim reward'),
      });
    } finally {
      setClaimingQuest(false);
    }
  };

  // Claim weekly bonus
  const claimWeeklyBonus = async () => {
    if (claimingWeekly || !questsData?.weekly?.can_claim) return;
    setClaimingWeekly(true);
    const tg = (window as any).Telegram?.WebApp;

    try {
      const response = await authedFetchJson(services, '/api/quests/weekly/claim', {
        method: 'POST',
      });

      if (response.ok && (response.data as any)?.ok) {
        await loadQuests();
        window.dispatchEvent(new Event('aicams-credits-refresh'));
        tg?.HapticFeedback?.notificationOccurred?.('success');
        setModal({
          show: true,
          type: 'success',
          message: t('quest.weekly_claimed', 'Weekly bonus claimed!'),
          reward: questsData.weekly.bonus_credits,
        });
      }
    } catch (err) {
      console.error('[TasksRoute] Failed to claim weekly:', err);
    } finally {
      setClaimingWeekly(false);
    }
  };
  
  // Instagram: track which tasks user clicked Subscribe on
  const [instagramSubscribed, setInstagramSubscribed] = useState<Set<number>>(new Set());
  // Instagram: track rejected tasks (can retry)
  const [rejectedTasks, setRejectedTasks] = useState<Set<number>>(new Set());
  // Instagram username input modal
  const [instagramModal, setInstagramModal] = useState<{ show: boolean; taskId: number | null; username: string }>({
    show: false,
    taskId: null,
    username: '',
  });

  const [countryModal, setCountryModal] = useState<{ show: boolean; taskId: number | null; search: string; selected: string | null }>({
    show: false,
    taskId: null,
    search: '',
    selected: null,
  });

  const filteredCountries = useMemo(() => {
    const q = countryModal.search.toLowerCase().trim();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(c =>
      c.name.toLowerCase().includes(q) || c.code.toLowerCase() === q
    );
  }, [countryModal.search]);

  // Available tasks = all active tasks that user hasn't completed or pending
  // Rejected tasks are shown as available (can retry)
  const availableTasks = useMemo(() => {
    return tasks.filter((t) => !completed.has(t.id) && !pendingTasks.has(t.id));
  }, [tasks, completed, pendingTasks]);
  
  // Pending tasks (instagram_follow awaiting admin verification)
  const pendingTasksList = useMemo(() => {
    return tasks.filter((t) => pendingTasks.has(t.id));
  }, [tasks, pendingTasks]);

  // Completed tasks with their data
  const completedTasksList = useMemo(() => {
    return tasks.filter((t) => completed.has(t.id));
  }, [tasks, completed]);

  // Load tasks function (can be called on mount and on visibility change)
  const loadTasks = async () => {
    setLoading(true);
    setErr(null);

    const telegramId = getTelegramId();
    if (!telegramId) {
      setErr(reportHandledError({
        err: new Error('telegram_user_missing'),
        kind: 'auth',
        code: 'telegram.user_missing',
        userMessage: t('telegram.user_missing', 'Telegram user not available'),
        severity: 'warning',
        context: { feature: 'tasks' },
      }));
      setLoading(false);
      return;
    }

    const hasJwt = await services.supabase.waitForJwt({ timeoutMs: 3000 });
    if (!hasJwt) {
      // Don't show error for JWT timeout - just skip loading
      setLoading(false);
      return;
    }

    try {
      const accessToken = services.supabase.getAccessToken();
      if (!accessToken) {
        setLoading(false);
        return;
      }

      const data = await authedApiJsonOrThrow<any>(services, '/api/tasks', undefined, {
        timeoutMs: 5000,
        ctx: { feature: 'tasks', endpoint: '/api/tasks' },
      });

      const taskRows = (data?.tasks || []) as TaskRow[];
      const completedRows = (data?.completed || []) as CompletedTask[];
      const pendingRows = (data?.pending || []) as PendingTask[];
      const rejectedRows = (data?.rejected || []) as PendingTask[];
      
      const completedSet = new Set<number>((completedRows || []).map((r: any) => Number(r.task_id)));
      const pendingMap = new Map<number, PendingTask>();
      for (const p of pendingRows) {
        pendingMap.set(Number(p.task_id), p);
      }
      const rejectedSet = new Set<number>(rejectedRows.map((r: any) => Number(r.task_id)));
      
      setCompletedTasks(completedRows);
      setCompleted(completedSet);
      setPendingTasks(pendingMap);
      setRejectedTasks(rejectedSet);
      setTasks(taskRows);
    } catch (e) {
      console.error('Tasks load exception:', e);
      setErr(reportHandledError({
        err: e,
        kind: 'api',
        code: 'tasks.load_exception',
        userMessage: t('tasks.error', 'Error loading tasks'),
        severity: 'error',
        context: { feature: 'tasks', endpoint: '/api/tasks' },
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
    loadQuests();

    // Reload tasks when returning to the app (but not during active submission)
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !awardingTaskId) {
        // Small delay to let any pending operations complete
        setTimeout(() => {
          if (!awardingTaskId) {
            loadTasks();
            loadQuests();
          }
        }, 300);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [awardingTaskId, loadQuests]);

  // Открыть ссылку/канал
  function openTaskUrl(task: TaskRow) {
    const tg = (window as any).Telegram?.WebApp;
    // Prefer server-signed outbound_url (unified redirect + logging)
    const taskUrl = String(task.outbound_url || task.url || '').trim();
    
    if (!taskUrl) {
      setModal({
        show: true,
        type: 'error',
        message: t('tasks.no_url', 'Task URL is not configured'),
      });
      return;
    }

    try {
      const isTelegramLink = /^https?:\/\/(t\.me|telegram\.me)\//i.test(taskUrl);
      if (isTelegramLink && tg?.openTelegramLink) {
        tg.openTelegramLink(taskUrl);
      } else if (tg?.openLink) {
        tg.openLink(taskUrl);
      } else {
        window.open(taskUrl, '_blank');
      }
      tg?.HapticFeedback?.impactOccurred?.('light');
    } catch (e) {
      console.error('openTaskUrl error:', e, taskUrl);
      // Fallback: try window.open
      try {
        window.open(taskUrl, '_blank');
      } catch {
        setModal({
          show: true,
          type: 'error',
          message: t('tasks.open_link_failed', 'Failed to open link. Please try again.'),
        });
      }
    }
  }

  // Открыть Instagram и отметить как subscribed
  function openInstagramAndMarkSubscribed(task: TaskRow) {
    openTaskUrl(task);
    // Mark as subscribed so Verify button becomes active
    setInstagramSubscribed((prev) => new Set(prev).add(task.id));
  }

  // Показать модалку для ввода Instagram username
  function showInstagramVerifyModal(task: TaskRow) {
    setInstagramModal({
      show: true,
      taskId: task.id,
      username: '',
    });
  }

  // Проверка подписки на канал с показом модалки
  async function verifyChannelSubscription(task: TaskRow) {
    const tg = (window as any).Telegram?.WebApp;
    setAwardingTaskId(task.id);
    
    try {
      const telegramId = getTelegramId();
      if (!telegramId) {
        setModal({
          show: true,
          type: 'error',
          message: reportHandledError({
            err: new Error('telegram_user_missing'),
            kind: 'auth',
            code: 'telegram.user_missing',
            userMessage: t('telegram.user_missing', 'Telegram user not available'),
            severity: 'warning',
            context: { feature: 'tasks', action: 'verifyChannelSubscription' },
          }),
        });
        return;
      }

      const hasJwt = await services.supabase.waitForJwt({ timeoutMs: 3000 });
      if (!hasJwt) {
        setModal({
          show: true,
          type: 'error',
          message: reportHandledError({
            err: new Error('auth_not_ready'),
            kind: 'auth',
            code: 'auth.not_ready',
            userMessage: t('auth.not_ready', 'Auth not ready yet'),
            severity: 'warning',
            context: { feature: 'tasks', action: 'verifyChannelSubscription' },
          }),
        });
        return;
      }

      const accessToken = services.supabase.getAccessToken();
      if (!accessToken) {
        setModal({
          show: true,
          type: 'error',
          message: reportHandledError({
            err: new Error('auth_required'),
            kind: 'auth',
            code: 'auth.required',
            userMessage: t('tasks.error', 'Error'),
            severity: 'warning',
            context: { feature: 'tasks', action: 'verifyChannelSubscription' },
          }),
        });
        return;
      }

      const fr = await authedFetchJson<any>(services, '/api/tasks/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskId: task.id }),
      }, {
        timeoutMs: 8000,
        ctx: { feature: 'tasks', action: 'verifyChannelSubscription', endpoint: '/api/tasks/complete' },
      });
      if (!fr.ok) throw new Error(`HTTP ${fr.status || 0}`);
      const out: any = fr.data;

      // Бизнес-результат: не подписан на канал — показываем дружелюбное сообщение
      if (!out?.ok && out?.reason === 'not_subscribed') {
        tg?.HapticFeedback?.notificationOccurred?.('warning');
        setModal({
          show: true,
          type: 'error',
          message: t('tasks.not_subscribed_modal', 'Please subscribe to the channel and try again'),
          actions: [
            {
              kind: 'primary',
              label: t('tasks.open_channel', 'Open channel'),
              onClick: () => {
                try { setModal((m) => ({ ...m, show: false })); } catch {}
                openTaskUrl(task);
              },
            },
          ],
        });
        return;
      }

      // Бизнес-результат: требуется Telegram аккаунт
      if (!out?.ok && out?.reason === 'telegram_required') {
        tg?.HapticFeedback?.notificationOccurred?.('warning');
        setModal({
          show: true,
          type: 'error',
          message: t('tasks.telegram_required', 'Telegram account is required for this task'),
        });
        return;
      }

      // Другие не-ok ответы — реальные ошибки
      if (!out?.ok) {
        tg?.HapticFeedback?.notificationOccurred?.('error');
        setModal({
          show: true,
          type: 'error',
          message: reportHandledError({
            err: new Error(out?.error || 'api_error'),
            kind: 'api',
            code: 'tasks.complete_failed_modal',
            userMessage: t('tasks.error', 'Error completing task'),
            severity: 'error',
            context: { feature: 'tasks', action: 'verifyChannelSubscription', endpoint: '/api/tasks/complete', resp: out },
          }),
        });
        return;
      }

      // Успех!
      setCompleted((prev) => new Set(prev).add(task.id));
      setCompletedTasks((prev) => {
        const row = out?.completed as CompletedTask | null;
        if (!row) return prev;
        const exists = prev.some((x) => Number(x.task_id) === Number(row.task_id));
        return exists ? prev : [row, ...prev];
      });

      // Note: task_completed is tracked server-side in /api/tasks/complete

      try { window.dispatchEvent(new Event('aicams-credits-refresh')); } catch {}
      tg?.HapticFeedback?.notificationOccurred?.('success');
      
      setModal({ 
        show: true, 
        type: 'success', 
        message: t('tasks.credits_awarded', 'Credits awarded!'),
        reward: task.reward_credits
      });
    } catch (e) {
      // Реальные ошибки (сеть, таймаут, и т.д.)
      setModal({
        show: true,
        type: 'error',
        message: reportHandledError({
          err: e,
          kind: 'api',
          code: 'tasks.complete_exception_modal',
          userMessage: t('tasks.error', 'Error completing task'),
          severity: 'error',
          context: { feature: 'tasks', action: 'verifyChannelSubscription', endpoint: '/api/tasks/complete' },
        }),
      });
    } finally {
      setAwardingTaskId(null);
    }
  }

  async function handleOpenTask(task: TaskRow) {
    if (completed.has(task.id)) return;
    
    const taskUrl = String(task.url || '').trim();
    if (!taskUrl) return;

    // Для остальных типов: сначала начисляем, потом открываем
    await awardTaskReward(task);
    await new Promise((r) => setTimeout(r, 100));
    openTaskUrl(task);
  }

  // Submit Instagram follow task with username from modal
  async function submitInstagramFollow() {
    const tg = (window as any).Telegram?.WebApp;
    const taskId = instagramModal.taskId;
    const username = String(instagramModal.username || '').trim().replace(/^@/, '');
    
    if (!taskId) return;
    
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;
    
    if (!username) {
      // Don't close modal, just show error
      tg?.HapticFeedback?.notificationOccurred?.('warning');
      return;
    }

    // Basic validation
    if (!/^[a-zA-Z0-9._]{1,30}$/.test(username)) {
      tg?.HapticFeedback?.notificationOccurred?.('warning');
      setModal({
        show: true,
        type: 'error',
        message: t('tasks.instagram_username_invalid', 'Invalid Instagram username format'),
      });
      return;
    }

    // Close modal and start submission
    setInstagramModal({ show: false, taskId: null, username: '' });
    setAwardingTaskId(task.id);

    try {
      const hasJwt = await services.supabase.waitForJwt({ timeoutMs: 3000 });
      if (!hasJwt) {
        setModal({
          show: true,
          type: 'error',
          message: t('auth.not_ready', 'Auth not ready yet'),
        });
        return;
      }

      const fr = await authedFetchJson<any>(services, '/api/tasks/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskId: task.id, instagram_username: username }),
      }, {
        timeoutMs: 8000,
        ctx: { feature: 'tasks', action: 'submitInstagramFollow', endpoint: '/api/tasks/complete' },
      });

      if (!fr.ok) throw new Error(`HTTP ${fr.status || 0}`);
      const out: any = fr.data;

      // Username required
      if (!out?.ok && out?.reason === 'instagram_username_required') {
        setModal({
          show: true,
          type: 'error',
          message: t('tasks.instagram_username_required', 'Please enter a valid Instagram username'),
        });
        return;
      }

      // Already submitted or completed
      if (out?.ok && (out?.alreadySubmitted || out?.alreadyCompleted)) {
        if (out?.status === 'pending') {
          setPendingTasks((prev) => {
            const next = new Map(prev);
            next.set(task.id, {
              task_id: task.id,
              status: 'pending',
              instagram_username: out.instagram_username || username,
              completed_at: new Date().toISOString(),
            });
            return next;
          });
          tg?.HapticFeedback?.notificationOccurred?.('success');
          setModal({
            show: true,
            type: 'success',
            message: t('tasks.instagram_pending', 'Your submission is awaiting verification'),
          });
        }
        return;
      }

      // Success - new pending submission
      if (out?.ok && out?.status === 'pending') {
        setPendingTasks((prev) => {
          const next = new Map(prev);
          next.set(task.id, {
            task_id: task.id,
            status: 'pending',
            instagram_username: username,
            completed_at: new Date().toISOString(),
          });
          return next;
        });
        // Clear subscribed and rejected states
        setInstagramSubscribed((prev) => {
          const next = new Set(prev);
          next.delete(task.id);
          return next;
        });
        setRejectedTasks((prev) => {
          const next = new Set(prev);
          next.delete(task.id);
          return next;
        });
        tg?.HapticFeedback?.notificationOccurred?.('success');
        setModal({
          show: true,
          type: 'success',
          message: t('tasks.instagram_submitted', 'Submission received! Awaiting admin verification.'),
        });
        return;
      }

      // Other errors
      if (!out?.ok) {
        const errorMsg = out?.message || out?.error || t('tasks.error', 'Error completing task');
        tg?.HapticFeedback?.notificationOccurred?.('error');
        setModal({
          show: true,
          type: 'error',
          message: errorMsg,
        });
      }
    } catch (e: any) {
      console.error('submitInstagramFollow error:', e);
      tg?.HapticFeedback?.notificationOccurred?.('error');
      const errorMsg = e?.message?.includes('timeout') 
        ? t('tasks.timeout_error', 'Request timed out. Please try again.')
        : e?.message?.includes('network') || e?.message?.includes('fetch')
          ? t('tasks.network_error', 'Network error. Check your connection.')
          : t('tasks.error', 'Error completing task');
      setModal({
        show: true,
        type: 'error',
        message: errorMsg,
      });
    } finally {
      setAwardingTaskId(null);
    }
  }

  async function submitCountrySelect() {
    const tg = (window as any).Telegram?.WebApp;
    const taskId = countryModal.taskId;
    const cc = countryModal.selected;
    if (!taskId || !cc) return;

    try {
      setAwardingTaskId(taskId);
      const hasJwt = await services.supabase.waitForJwt({ timeoutMs: 3000 });
      if (!hasJwt) return;

      const fr = await authedFetchJson<any>(services, '/api/tasks/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskId, country_code: cc }),
      }, {
        timeoutMs: 8000,
        ctx: { feature: 'tasks', action: 'submitCountrySelect', endpoint: '/api/tasks/complete' },
      });

      if (!fr.ok) throw new Error(`HTTP ${fr.status || 0}`);
      const out: any = fr.data;

      if (!out?.ok && out?.reason === 'invalid_country_code') {
        tg?.HapticFeedback?.notificationOccurred?.('warning');
        setModal({ show: true, type: 'error', message: t('tasks.invalid_country', 'Please select a valid country') });
        return;
      }

      if (out?.ok) {
        setCountryModal({ show: false, taskId: null, search: '', selected: null });
        setCompleted(prev => new Set([...prev, taskId]));
        tg?.HapticFeedback?.notificationOccurred?.('success');
        if (out.reward > 0) {
          setModal({
            show: true,
            type: 'success',
            message: `+${out.reward} ${t('tasks.credits', 'credits')}!`,
          });
        }
      }
    } catch (e: any) {
      console.error('submitCountrySelect error:', e);
      tg?.HapticFeedback?.notificationOccurred?.('error');
      setModal({
        show: true,
        type: 'error',
        message: t('tasks.error', 'Error completing task'),
      });
    } finally {
      setAwardingTaskId(null);
    }
  }

  async function awardTaskReward(task: TaskRow): Promise<boolean> {
    try {
      const tg = (window as any).Telegram?.WebApp;
      const telegramId = getTelegramId();
      if (!telegramId) {
        setErr(reportHandledError({
          err: new Error('telegram_user_missing'),
          kind: 'auth',
          code: 'telegram.user_missing',
          userMessage: t('telegram.user_missing', 'Telegram user not available'),
          severity: 'warning',
          context: { feature: 'tasks', action: 'awardTaskReward' },
        }));
        return false;
      }
      if (completed.has(task.id)) return true;

      const hasJwt = await services.supabase.waitForJwt({ timeoutMs: 3000 });
      if (!hasJwt) {
        setErr(reportHandledError({
          err: new Error('auth_not_ready'),
          kind: 'auth',
          code: 'auth.not_ready',
          userMessage: t('auth.not_ready', 'Auth not ready yet'),
          severity: 'warning',
          context: { feature: 'tasks', action: 'awardTaskReward' },
        }));
        return false;
      }

      setAwardingTaskId(task.id);
      setErr(null);

      const accessToken = services.supabase.getAccessToken();
      if (!accessToken) throw new Error('JWT missing');

      const fr = await authedFetchJson<any>(services, '/api/tasks/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskId: task.id }),
      }, {
        timeoutMs: 8000,
        ctx: { feature: 'tasks', action: 'awardTaskReward', endpoint: '/api/tasks/complete' },
      });
      if (!fr.ok) throw new Error(`HTTP ${fr.status || 0}`);
      const out: any = fr.data;

      // Бизнес-результаты (не ошибки)
      if (!out?.ok && out?.reason === 'not_subscribed') {
        setErr(t('tasks.not_subscribed', 'Please subscribe to the channel first'));
        tg?.HapticFeedback?.notificationOccurred?.('warning');
        return false;
      }
      if (!out?.ok && out?.reason === 'telegram_required') {
        setErr(t('tasks.telegram_required', 'Telegram account is required for this task'));
        tg?.HapticFeedback?.notificationOccurred?.('warning');
        return false;
      }

      // Реальные ошибки
      if (!out?.ok) {
        setErr(reportHandledError({
          err: new Error(out?.error || 'complete_failed'),
          kind: 'api',
          code: 'tasks.complete_failed',
          userMessage: t('tasks.error', 'Error completing task'),
          severity: 'error',
          context: { feature: 'tasks', action: 'awardTaskReward', endpoint: '/api/tasks/complete', resp: out },
        }));
        return false;
      }

      // Update local completed state (idempotent)
      setCompleted((prev) => new Set(prev).add(task.id));
      setCompletedTasks((prev) => {
        // Keep completed rows if backend returned one
        const row = out?.completed as CompletedTask | null;
        if (!row) return prev;
        const exists = prev.some((x) => Number(x.task_id) === Number(row.task_id));
        return exists ? prev : [row, ...prev];
      });

      // Note: task_completed is tracked server-side in /api/tasks/complete

      // Request immediate credits refresh in AppShell (header balance).
      try {
        window.dispatchEvent(new Event('aicams-credits-refresh'));
      } catch {}
      
      // Haptic feedback
      tg?.HapticFeedback?.notificationOccurred?.('success');
      return true;
    } catch (e: any) {
      // Реальные ошибки (сеть, таймаут, и т.д.)
      setErr(reportHandledError({
        err: e,
        kind: 'api',
        code: 'tasks.complete_failed',
        userMessage: t('tasks.error', 'Error completing task'),
        severity: 'error',
        context: { feature: 'tasks', action: 'awardTaskReward', endpoint: '/api/tasks/complete' },
      }));
      return false;
    } finally {
      setAwardingTaskId(null);
    }
  }

  // Combine available and pending for the "available" tab
  const displayTasks = activeTab === 'available' 
    ? [...pendingTasksList, ...availableTasks] 
    : completedTasksList;

  const closeModal = () => setModal({ ...modal, show: false });

  return (
    <div>
      {/* Модалка результата */}
      {modal.show && (
        <div 
          class="modalOverlay" 
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: 20,
          }}
          onClick={closeModal}
        >
          <div 
            class="modalContent"
            style={{
              background: 'linear-gradient(145deg, #1a1a2e 0%, #16213e 100%)',
              borderRadius: 16,
              padding: 24,
              maxWidth: 320,
              width: '100%',
              textAlign: 'center',
              border: modal.type === 'success' 
                ? '1px solid rgba(50,205,50,0.4)' 
                : '1px solid rgba(255,90,95,0.4)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: 48, marginBottom: 16 }}>
              {modal.type === 'success' ? '✅' : '❌'}
            </div>
            <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 8 }}>
              {modal.type === 'success' 
                ? t('tasks.success_title', 'Success!') 
                : t('tasks.error_title', 'Not subscribed')}
            </div>
            <div style={{ opacity: 0.85, marginBottom: 16 }}>
              {modal.message}
            </div>
            {modal.type === 'success' && modal.reward && (
              <div style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: 8, 
                fontSize: 24, 
                fontWeight: 900,
                color: 'rgba(50,205,50,0.95)',
                marginBottom: 16,
              }}>
                +{modal.reward} <IconStar class="starSvg" size={24} filled />
              </div>
            )}
            {(modal.actions && modal.actions.length > 0) ? (
              <div style={{ display: 'grid', gap: 10 }}>
                {modal.actions.map((a, idx) => (
                  <button
                    key={idx}
                    class="taskActionBtn"
                    style={{
                      width: '100%',
                      background:
                        (a.kind === 'primary')
                          ? 'linear-gradient(135deg, rgba(120,120,255,0.45), rgba(90,90,200,0.35))'
                          : 'rgba(255,255,255,0.1)',
                      border: 0,
                    }}
                    onClick={() => {
                      try { a.onClick(); } catch {}
                    }}
                  >
                    {a.label}
                  </button>
                ))}
                <button
                  class="taskActionBtn"
                  style={{
                    width: '100%',
                    background: 'rgba(255,255,255,0.1)',
                    border: 0,
                  }}
                  onClick={closeModal}
                >
                  {t('btn.ok', 'OK')}
                </button>
              </div>
            ) : (
              <button
                class="taskActionBtn"
                style={{
                  width: '100%',
                  background: modal.type === 'success'
                    ? 'linear-gradient(135deg, rgba(50,205,50,0.55), rgba(34,139,34,0.45))'
                    : 'rgba(255,255,255,0.1)',
                  border: 0,
                }}
                onClick={closeModal}
              >
                {t('btn.ok', 'OK')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Модалка для ввода Instagram username */}
      {instagramModal.show && (
        <div 
          class="modalOverlay" 
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: 20,
          }}
          onClick={() => setInstagramModal({ show: false, taskId: null, username: '' })}
        >
          <div 
            class="modalContent"
            style={{
              background: 'linear-gradient(145deg, #1a1a2e 0%, #16213e 100%)',
              borderRadius: 16,
              padding: 24,
              maxWidth: 320,
              width: '100%',
              textAlign: 'center',
              border: '1px solid rgba(120,120,255,0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: 32, marginBottom: 12 }}>📸</div>
            <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 8 }}>
              {t('tasks.enter_instagram', 'Enter your Instagram')}
            </div>
            <div style={{ opacity: 0.8, marginBottom: 16, fontSize: 14 }}>
              {t('tasks.instagram_hint', 'Enter the username you used to follow')}
            </div>
            <input
              type="text"
              placeholder="@username"
              value={instagramModal.username}
              onInput={(e) => {
                const val = (e.target as HTMLInputElement).value;
                setInstagramModal((prev) => ({ ...prev, username: val }));
              }}
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 10,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.08)',
                color: 'inherit',
                fontSize: 16,
                outline: 'none',
                marginBottom: 16,
                textAlign: 'center',
                boxSizing: 'border-box',
              }}
              autoFocus
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                class="taskActionBtn"
                style={{
                  flex: 1,
                  background: 'rgba(255,255,255,0.1)',
                  border: 0,
                }}
                onClick={() => setInstagramModal({ show: false, taskId: null, username: '' })}
              >
                {t('btn.cancel', 'Cancel')}
              </button>
              <button
                class="taskActionBtn"
                style={{
                  flex: 1,
                  background: instagramModal.username.trim() 
                    ? 'linear-gradient(135deg, rgba(50,205,50,0.55), rgba(34,139,34,0.45))'
                    : 'rgba(255,255,255,0.1)',
                  border: 0,
                  opacity: instagramModal.username.trim() ? 1 : 0.5,
                }}
                disabled={!instagramModal.username.trim()}
                onClick={() => submitInstagramFollow()}
              >
                ✓ {t('tasks.confirm', 'Confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {countryModal.show && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: 20,
          }}
          onClick={() => setCountryModal({ show: false, taskId: null, search: '', selected: null })}
        >
          <div
            style={{
              background: 'linear-gradient(145deg, #1a1a2e 0%, #16213e 100%)',
              borderRadius: 16,
              padding: 24,
              maxWidth: 360,
              width: '100%',
              border: '1px solid rgba(120,120,255,0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: 32, marginBottom: 12, textAlign: 'center' }}>🌍</div>
            <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 8, textAlign: 'center' }}>
              {t('tasks.select_your_country', 'Select your country')}
            </div>
            <div style={{ opacity: 0.8, marginBottom: 16, fontSize: 14, textAlign: 'center' }}>
              {t('tasks.country_hint', 'Tell us where you are from')}
            </div>
            <input
              type="text"
              placeholder={t('tasks.search_country', 'Search country...')}
              value={countryModal.search}
              onInput={(e) => {
                const val = (e.target as HTMLInputElement).value;
                setCountryModal((prev) => ({ ...prev, search: val, selected: null }));
              }}
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 10,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.08)',
                color: 'inherit',
                fontSize: 15,
                outline: 'none',
                marginBottom: 12,
                boxSizing: 'border-box',
              }}
              autoFocus
            />
            <div
              style={{
                maxHeight: 260,
                overflowY: 'auto',
                marginBottom: 16,
                borderRadius: 10,
                border: '1px solid rgba(255,255,255,0.1)',
              }}
            >
              {filteredCountries.length === 0 ? (
                <div style={{ padding: 16, textAlign: 'center', opacity: 0.6, fontSize: 14 }}>
                  {t('tasks.no_countries_found', 'No countries found')}
                </div>
              ) : (
                filteredCountries.map((c) => (
                  <div
                    key={c.code}
                    onClick={() => setCountryModal((prev) => ({ ...prev, selected: c.code }))}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 12px',
                      cursor: 'pointer',
                      background: countryModal.selected === c.code
                        ? 'rgba(50,205,50,0.2)'
                        : 'transparent',
                      borderBottom: '1px solid rgba(255,255,255,0.06)',
                      transition: 'background 0.15s',
                    }}
                  >
                    <span style={{ fontSize: 22 }}>{countryFlag(c.code)}</span>
                    <span style={{ fontSize: 14, fontWeight: countryModal.selected === c.code ? 700 : 500 }}>
                      {c.name}
                    </span>
                    {countryModal.selected === c.code && (
                      <span style={{ marginLeft: 'auto', color: 'rgba(50,205,50,0.95)', fontWeight: 700 }}>✓</span>
                    )}
                  </div>
                ))
              )}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                class="taskActionBtn"
                style={{
                  flex: 1,
                  background: 'rgba(255,255,255,0.1)',
                  border: 0,
                }}
                onClick={() => setCountryModal({ show: false, taskId: null, search: '', selected: null })}
              >
                {t('btn.cancel', 'Cancel')}
              </button>
              <button
                class="taskActionBtn"
                style={{
                  flex: 1,
                  background: countryModal.selected
                    ? 'linear-gradient(135deg, rgba(50,205,50,0.55), rgba(34,139,34,0.45))'
                    : 'rgba(255,255,255,0.1)',
                  border: 0,
                  opacity: countryModal.selected ? 1 : 0.5,
                }}
                disabled={!countryModal.selected}
                onClick={() => submitCountrySelect()}
              >
                ✓ {t('tasks.confirm', 'Confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div class="row" style={{ marginBottom: 10 }}>
        <button class="iconBtn" onClick={() => requestAppBack()} aria-label={t('btn.back', 'Back')}>
          <IconBack />
        </button>
        <div style={{ fontWeight: 900 }}>{t('tasks.title', 'Tasks')}</div>
        <div />
      </div>

      {/* Tabs */}
      <div class="tabsRow" style={{ marginBottom: 12 }}>
        <button
          class={`tabBtn ${activeTab === 'available' ? 'active' : ''}`}
          onClick={() => setActiveTab('available')}
        >
          {t('tasks.available', 'Available')}
        </button>
        <button
          class={`tabBtn ${activeTab === 'completed' ? 'active' : ''}`}
          onClick={() => setActiveTab('completed')}
        >
          {t('tasks.completed_tab', 'Completed')} {completedTasksList.length > 0 && `(${completedTasksList.length})`}
        </button>
      </div>

      {/* ========== Daily Quest (shown at top of Available tab) ========== */}
      {activeTab === 'available' && questsData?.enabled && questsData.quest && (
        <div 
          class="dailyQuestCard" 
          style={{
            background: 'linear-gradient(135deg, rgba(138,43,226,0.25) 0%, rgba(75,0,130,0.35) 100%)',
            border: '1px solid rgba(138,43,226,0.4)',
            borderRadius: 16,
            padding: 16,
            marginBottom: 16,
          }}
        >
          {/* Quest Unavailable (legacy claimed quest) */}
          {questsData.quest.quest_unavailable ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
              <div style={{ fontWeight: 900, fontSize: 18, marginBottom: 8 }}>
                {t('quest.completed_title', 'Quest Completed!')}
              </div>
              <div style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: 6, 
                background: 'rgba(50,205,50,0.2)',
                padding: '8px 16px',
                borderRadius: 20,
                fontWeight: 800,
                color: 'rgba(50,205,50,0.95)',
              }}>
                <span>+{questsData.quest.credits_awarded}</span>
                <IconStar class="starSvg" size={16} filled />
              </div>
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 12 }}>
                {t('quest.check_tomorrow', 'Check back tomorrow for a new quest!')}
              </div>
            </div>
          ) : (
            <>
              {/* Quest Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 28 }}>🎁</span>
                  <div>
                    <div style={{ fontWeight: 900, fontSize: 16 }}>{questsData.quest.icon || '🎯'} {t(`quest.template.${questsData.quest.template_id}`, questsData.quest.title_key || '')}</div>
                    <div style={{ fontSize: 12, opacity: 0.8 }}>
                      {questsData.quest.objectives.filter(o => o.completed).length}/{questsData.quest.objectives.length} {t('quest.objectives_done', 'done')}
                    </div>
                  </div>
                </div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 6, 
                  background: 'rgba(255,215,0,0.2)',
                  padding: '6px 12px',
                  borderRadius: 20,
                  fontWeight: 800,
                }}>
                  <span>+{questsData.quest.reward_credits}</span>
                  <IconStar class="starSvg" size={16} filled />
                </div>
              </div>

              {/* Objectives */}
              <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
                {questsData.quest.objectives.map(obj => (
              <div 
                key={obj.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  background: obj.completed ? 'rgba(50,205,50,0.15)' : 'rgba(255,255,255,0.08)',
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: obj.completed ? '1px solid rgba(50,205,50,0.3)' : '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <span style={{ fontSize: 20 }}>{QUEST_ICONS[obj.icon] || '⭐'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
                    {t(`quest.objective.${obj.id}`, obj.title_key)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ 
                      flex: 1, 
                      height: 6, 
                      background: 'rgba(255,255,255,0.15)', 
                      borderRadius: 3,
                      overflow: 'hidden',
                    }}>
                      <div style={{ 
                        width: `${Math.min(100, (obj.current / obj.target) * 100)}%`,
                        height: '100%',
                        background: obj.completed 
                          ? 'linear-gradient(90deg, #32cd32, #228b22)'
                          : 'linear-gradient(90deg, #8b5cf6, #7c3aed)',
                        borderRadius: 3,
                        transition: 'width 0.3s ease',
                      }} />
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 600, opacity: 0.9, whiteSpace: 'nowrap' }}>
                      {obj.current}/{obj.target}
                    </span>
                  </div>
                </div>
                {obj.completed && <span style={{ fontSize: 16, color: 'rgba(50,205,50,0.95)' }}>✓</span>}
              </div>
            ))}
          </div>

          {/* Claim Button or Status */}
          {questsData.quest.already_claimed ? (
            <div style={{
              textAlign: 'center',
              padding: '10px 16px',
              background: 'rgba(50,205,50,0.15)',
              borderRadius: 10,
              fontWeight: 700,
              color: 'rgba(50,205,50,0.95)',
            }}>
              ✓ {t('quest.claimed', 'Claimed!')} +{questsData.quest.credits_awarded}
            </div>
          ) : questsData.quest.can_claim ? (
            <button
              onClick={() => claimQuestReward()}
              disabled={claimingQuest}
              style={{
                width: '100%',
                padding: '12px 20px',
                background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                border: 'none',
                borderRadius: 10,
                color: '#fff',
                fontWeight: 800,
                fontSize: 15,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              {claimingQuest ? (
                <>{t('quest.claiming', 'Claiming...')}</>
              ) : (
                <>🎁 {t('quest.claim_reward', 'Claim Reward')} +{questsData.quest.reward_credits}</>
              )}
            </button>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '10px 16px',
              background: 'rgba(255,255,255,0.08)',
              borderRadius: 10,
              fontSize: 13,
              opacity: 0.8,
            }}>
              {t('quest.complete_all', 'Complete all objectives to claim')}
            </div>
          )}
            </>
          )}

          {/* Re-enable popup button if disabled */}
          {popupDisabled && (
            <button
              onClick={() => {
                setNeverShow(false);
                setPopupDisabled(false);
              }}
              style={{
                display: 'block',
                margin: '12px auto 0',
                padding: '6px 12px',
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: 8,
                color: 'rgba(255,255,255,0.6)',
                fontSize: 11,
                cursor: 'pointer',
              }}
            >
              {t('quest.enable_popup', 'Enable quest popup')}
            </button>
          )}
        </div>
      )}

      {/* Quest loading state */}
      {activeTab === 'available' && questLoading && (
        <div style={{ 
          background: 'rgba(138,43,226,0.15)',
          borderRadius: 12,
          padding: 16,
          marginBottom: 16,
          textAlign: 'center',
        }}>
          <div class="spinner" style={{ margin: '0 auto' }} />
        </div>
      )}

      {/* No quest available */}
      {activeTab === 'available' && !questLoading && (!questsData?.enabled || !questsData?.quest) && (
        <div style={{ 
          background: 'rgba(138,43,226,0.15)',
          borderRadius: 12,
          padding: 24,
          textAlign: 'center',
          marginBottom: 16,
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🎁</div>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>{t('quest.no_quest_today', 'No quest available today')}</div>
          <div style={{ opacity: 0.7, fontSize: 13 }}>{t('quest.check_back', 'Check back tomorrow!')}</div>
        </div>
      )}

      {/* Weekly Streak */}
      {activeTab === 'available' && !questLoading && questsData?.weekly && (
        <div style={{ 
          background: 'linear-gradient(135deg, rgba(255,107,53,0.2), rgba(247,147,30,0.15))',
          border: '1px solid rgba(255,107,53,0.3)',
          borderRadius: 14,
          padding: 14,
          marginBottom: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 800 }}>
              <span style={{ fontSize: 20 }}>🔥</span>
              <span>{t('quest.weekly_streak', 'Weekly Streak')}</span>
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#f7931e' }}>
              {questsData.weekly.days_completed}/{questsData.weekly.required_days} {t('quest.days', 'days')}
            </span>
          </div>
          <div style={{ 
            height: 8, 
            background: 'rgba(255,255,255,0.12)', 
            borderRadius: 4,
            overflow: 'hidden',
            marginBottom: 10,
          }}>
            <div style={{ 
              width: `${Math.min(100, (questsData.weekly.days_completed / questsData.weekly.required_days) * 100)}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #ff6b35, #f7931e, #FFD700)',
              borderRadius: 4,
              boxShadow: '0 0 8px rgba(247,147,30,0.5)',
            }} />
          </div>
          {questsData.weekly.can_claim ? (
            <button
              onClick={() => claimWeeklyBonus()}
              disabled={claimingWeekly}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                border: 'none',
                borderRadius: 10,
                color: '#fff',
                fontWeight: 800,
                fontSize: 15,
                cursor: 'pointer',
                boxShadow: '0 4px 16px rgba(255,107,53,0.3)',
              }}
            >
              {claimingWeekly ? t('quest.claiming', 'Claiming...') : `🏆 ${t('quest.claim_bonus', 'Claim Bonus')} +${questsData.weekly.bonus_credits}`}
            </button>
          ) : questsData.weekly.already_claimed ? (
            <div style={{ textAlign: 'center', fontSize: 14, color: 'rgba(50,205,50,0.95)', fontWeight: 700 }}>
              ✓ {t('quest.bonus_claimed', 'Bonus claimed!')}
            </div>
          ) : (
            <div style={{ textAlign: 'center', fontSize: 12, opacity: 0.8 }}>
              {t('quest.weekly_hint', 'Complete daily quests for')} {questsData.weekly.required_days} {t('quest.days', 'days')} {t('quest.for_bonus', 'to get')} +{questsData.weekly.bonus_credits}
            </div>
          )}
        </div>
      )}

      {/* ========== Tasks List ========== */}
      {loading && <div class="muted" style={{ padding: 12 }}>{t('tasks.loading', 'Loading tasks...')}</div>}
      {err && (
        <div class="card" style={{ padding: 12, borderColor: 'rgba(255,90,95,0.35)' }}>
          <div style={{ color: 'rgba(255,90,95,0.95)', fontWeight: 800 }}>{err}</div>
        </div>
      )}

      {!loading && !err && displayTasks.length === 0 && (
        <div class="muted" style={{ padding: 12, textAlign: 'center' }}>
          {activeTab === 'available'
            ? t('tasks.no_available', 'No tasks available')
            : t('tasks.no_completed', 'No completed tasks yet')}
        </div>
      )}

      {!loading && !err && displayTasks.length > 0 && (
        <div style={{ display: 'grid', gap: 10 }}>
          {displayTasks.map((taskRow) => {
            const done = completed.has(taskRow.id);
            const isPending = pendingTasks.has(taskRow.id);
            const pendingInfo = pendingTasks.get(taskRow.id);
            // Prefer centralized translations (namespace: tasks). Fallback to RU source from tasks table.
            const title = t(taskTitleKey(taskRow.id), String(taskRow.title || ''));
            const desc = t(taskDescriptionKey(taskRow.id), String(taskRow.description || ''));
            const taskUrl = String(taskRow.url || '').trim();
            const isAwarding = awardingTaskId === taskRow.id;
            const isTelegramFolder = taskRow.type === 'telegram_channel_folder';
            const folderChannelCount = taskFolderChannelCount(taskRow);
            const folderChannelText = folderChannelCount === null
              ? t('tasks.multiple_channels', 'Multiple channels')
              : t(
                  folderChannelCount === 1 ? 'tasks.channel_count_one' : 'tasks.channel_count_many',
                  folderChannelCount === 1 ? '{count} channel' : '{count} channels'
                ).replace('{count}', String(folderChannelCount));

            return (
              <div
                key={taskRow.id}
                class="card"
                style={{ padding: 12, opacity: done ? 0.85 : 1 }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{ fontWeight: 800 }}>{title}</div>
                  <div style={{ fontWeight: 900, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span>{done ? '+' : ''}{taskRow.reward_credits}</span>
                    <IconStar class="starSvg" size={18} filled />
                  </div>
                </div>
                {desc && <div style={{ marginTop: 6, opacity: 0.8 }}>{desc}</div>}
                {isTelegramFolder && (
                  <div
                    style={{
                      marginTop: 8,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      maxWidth: '100%',
                      minHeight: 28,
                      padding: '5px 10px',
                      borderRadius: 999,
                      background: 'rgba(42,171,238,0.12)',
                      border: '1px solid rgba(42,171,238,0.24)',
                      color: 'rgba(225,247,255,0.95)',
                      fontSize: 12,
                      fontWeight: 800,
                      lineHeight: 1,
                      boxSizing: 'border-box',
                    }}
                    aria-label={`${t('tasks.folder', 'Folder')}: ${folderChannelText}`}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        position: 'relative',
                        width: 18,
                        height: 14,
                        flex: '0 0 18px',
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 4,
                          width: 12,
                          height: 8,
                          borderRadius: 3,
                          border: '1px solid rgba(225,247,255,0.75)',
                          background: 'rgba(42,171,238,0.18)',
                        }}
                      />
                      <span
                        style={{
                          position: 'absolute',
                          right: 0,
                          top: 0,
                          width: 12,
                          height: 8,
                          borderRadius: 3,
                          border: '1px solid rgba(225,247,255,0.9)',
                          background: 'rgba(42,171,238,0.3)',
                        }}
                      />
                    </span>
                    <span style={{ whiteSpace: 'nowrap' }}>{t('tasks.folder', 'Folder')}</span>
                    <span style={{ width: 3, height: 3, borderRadius: 999, background: 'rgba(225,247,255,0.55)', flex: '0 0 auto' }} />
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {folderChannelText}
                    </span>
                  </div>
                )}

                <div class="taskActionsRow">
                  {done ? (
                    <button class="taskActionBtn taskActionBtnComplete done" disabled>
                      {t('tasks.completed', 'Completed')} ✓
                    </button>
                  ) : isPending ? (
                    /* Pending verification - show status */
                    <div style={{ width: '100%' }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 8, 
                        padding: '8px 12px',
                        background: 'rgba(255,193,7,0.15)',
                        borderRadius: 8,
                        border: '1px solid rgba(255,193,7,0.3)',
                      }}>
                        <span style={{ fontSize: 16 }}>⏳</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: 13, color: 'rgba(255,193,7,0.95)' }}>
                            {t('tasks.pending_verification', 'Awaiting verification')}
                          </div>
                          {pendingInfo?.instagram_username && (
                            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>
                              @{pendingInfo.instagram_username}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : taskRow.type === 'telegram_channel' || taskRow.type === 'telegram_channel_folder' ? (
                    /* Для telegram_channel / telegram_channel_folder: две кнопки - Subscribe и Verify */
                    <>
                      <button
                        class="taskActionBtn taskActionBtnOpen"
                        disabled={!taskUrl}
                        onClick={() => openTaskUrl(taskRow)}
                        style={{ flex: 1 }}
                      >
                        {t('tasks.subscribe', 'Subscribe')}
                      </button>
                      <button
                        class={`taskActionBtn taskActionBtnVerify ${isAwarding ? 'disabled' : ''}`}
                        disabled={isAwarding}
                        onClick={() => verifyChannelSubscription(taskRow)}
                        style={{ flex: 1 }}
                      >
                        {isAwarding ? t('tasks.verifying', 'Verifying…') : t('tasks.verify', 'Verify')}
                      </button>
                    </>
                  ) : taskRow.type === 'instagram_follow' ? (
                    /* Для instagram_follow: две кнопки как у telegram_channel */
                    (() => {
                      const isRejected = rejectedTasks.has(taskRow.id);
                      // For rejected tasks, Verify is always enabled (can retry)
                      const verifyEnabled = isRejected || instagramSubscribed.has(taskRow.id);
                      return (
                        <>
                          <button
                            class="taskActionBtn taskActionBtnOpen"
                            disabled={!taskUrl}
                            onClick={() => openInstagramAndMarkSubscribed(taskRow)}
                            style={{ flex: 1 }}
                          >
                            {t('tasks.subscribe', 'Subscribe')}
                          </button>
                          <button
                            class={`taskActionBtn taskActionBtnVerify ${isAwarding || !verifyEnabled ? 'disabled' : ''}`}
                            disabled={isAwarding || !verifyEnabled}
                            onClick={() => showInstagramVerifyModal(taskRow)}
                            style={{ flex: 1 }}
                          >
                            {isAwarding 
                              ? t('tasks.verifying', 'Verifying…') 
                              : isRejected 
                                ? t('tasks.try_again', 'Try Again')
                                : t('tasks.verify', 'Verify')}
                          </button>
                        </>
                      );
                    })()
                  ) : taskRow.type === 'country_select' ? (
                    <button
                      class={`taskActionBtn taskActionBtnOpen ${isAwarding ? 'disabled' : ''}`}
                      disabled={isAwarding}
                      onClick={() => setCountryModal({ show: true, taskId: taskRow.id, search: '', selected: null })}
                    >
                      🌍 {t('tasks.select_country', 'Select Country')}
                    </button>
                  ) : (
                    <button
                      class={`taskActionBtn taskActionBtnOpen ${!taskUrl || isAwarding ? 'disabled' : ''}`}
                      disabled={!taskUrl || isAwarding}
                      onClick={() => handleOpenTask(taskRow)}
                    >
                      {isAwarding ? t('tasks.completing', 'Completing…') : t('tasks.open', 'Open')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
